<template>
  <div class="ctc-step">
    <div class="ctc-step__eyebrow">PHASE 2 · ÉTAPE 3 SUR 4</div>
    <h2 class="ctc-step__title">Paramètres de l’article</h2>
    <section class="ctc-settings" aria-label="Paramètres de l’article">
      <div v-if="site" class="ctc-settings__row">
        <span class="ctc-settings__icon">
          <v-icon :color="indigo">{{ icons.mdiWeb }}</v-icon>
        </span>
        <span class="ctc-settings__copy">
          <strong>Site de publication</strong>
          <small
            >Il détermine le média Affilizz et le CMS de destination.</small
          >
        </span>
        <div class="ctc-settings__control ctc-settings__site">
          <span class="ctc-settings__site-name">
            <strong>{{ site.name }}</strong>
            <small>{{ site.domain }}</small>
          </span>
          <rmr-action-button
            label="Modifier"
            :icon="icons.mdiPencil"
            variant="ghost"
            size="sm"
            @click="$emit('edit-site')"
          />
        </div>
      </div>

      <div class="ctc-settings__row">
        <span class="ctc-settings__icon">
          <v-icon :color="indigo">{{ icons.mdiTextBoxEditOutline }}</v-icon>
        </span>
        <span class="ctc-settings__copy">
          <strong>Format éditorial</strong>
          <small>{{ formatHelp }}</small>
        </span>
        <div class="ctc-settings__control ctc-publication">
          <rmr-segmented-control
            :value="editorialFormat"
            :items="formatOptions"
            size="md"
            block
            aria-label="Format éditorial"
            @input="$emit('update:editorialFormat', $event)"
          />
          <span
            v-if="editorialFormatError"
            class="ctc-publication__error"
            role="alert"
          >
            {{ editorialFormatError }}
          </span>
        </div>
      </div>

      <div class="ctc-settings__row">
        <span class="ctc-settings__icon">
          <v-icon :color="indigo">{{ icons.mdiAccountEditOutline }}</v-icon>
        </span>
        <span class="ctc-settings__copy">
          <strong>Signature</strong>
          <small>Préremplie selon le site, modifiable pour cet article.</small>
        </span>
        <div class="ctc-settings__control">
          <rmr-text-field
            :value="signature"
            :counter="signatureMaxLength"
            :error-messages="signatureError ? [signatureError] : []"
            :hide-details="!signatureError"
            aria-label="Signature de l’article"
            @input="$emit('update:signature', $event)"
          />
        </div>
      </div>

      <div class="ctc-settings__row">
        <span class="ctc-settings__icon">
          <v-icon :color="indigo">{{ icons.mdiPublish }}</v-icon>
        </span>
        <span class="ctc-settings__copy">
          <strong>Publication dans le CMS</strong>
          <small>{{ publicationHelp }}</small>
        </span>
        <div class="ctc-settings__control ctc-publication">
          <rmr-segmented-control
            :value="postStatus"
            :items="postStatusOptions"
            size="md"
            block
            aria-label="Mode de publication dans le CMS"
            @input="$emit('update:postStatus', $event)"
          />

          <div v-if="postStatus === 'future'" class="ctc-publication__schedule">
            <rmr-date-picker-field
              :value="scheduledDate"
              :display-value="scheduledDateLabel"
              close-on-input
              label="Date"
              prepend-inner-icon="mdi-calendar"
              :picker-props="{ min: today, firstDayOfWeek: 1 }"
              @input="$emit('update:schedule', { date: $event })"
            />
            <rmr-select
              :value="scheduledTime"
              :items="timeOptions"
              label="Heure"
              prepend-inner-icon="mdi-clock-outline"
              :menu-props="{ maxHeight: 260 }"
              @input="$emit('update:schedule', { time: $event })"
            />
          </div>

          <span
            v-if="scheduleError"
            class="ctc-publication__error"
            role="alert"
          >
            {{ scheduleError }}
          </span>
          <span
            v-else-if="postStatus !== 'draft'"
            class="ctc-publication__warning"
          >
            <v-icon small :color="warningColor">
              {{ icons.mdiAlertOutline }}
            </v-icon>
            L’article sera mis en ligne sans relecture préalable.
          </span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed } from "@vue/composition-api";
import {
  mdiAccountEditOutline,
  mdiAlertOutline,
  mdiPencil,
  mdiPublish,
  mdiTextBoxEditOutline,
  mdiWeb,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrDatePickerField from "@/components/ui/forms/RmrDatePickerField.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import { RMR_INDIGO, RMR_ORANGE } from "@/styles/tokens";

import {
  EDITORIAL_FORMAT_DEAL,
  EDITORIAL_FORMAT_OPTIONS,
  POST_STATUS_OPTIONS,
  SIGNATURE_MAX_LENGTH,
} from "../constants";
import {
  localDateKey,
  requestedPublicationLabel,
  scheduleTimeOptions,
} from "../ctc-model";

export default {
  name: "CtcSettingsStep",
  components: {
    RmrActionButton,
    RmrDatePickerField,
    RmrSegmentedControl,
    RmrSelect,
    RmrTextField,
  },
  props: {
    site: {
      type: Object,
      default: null,
    },
    signature: {
      type: String,
      default: "",
    },
    signatureError: {
      type: String,
      default: "",
    },
    editorialFormat: {
      type: String,
      default: "focus_product",
    },
    // Why the chosen Bon plan can not be created with these prices.
    editorialFormatError: {
      type: String,
      default: "",
    },
    postStatus: {
      type: String,
      default: "draft",
    },
    scheduledDate: {
      type: String,
      default: null,
    },
    scheduledTime: {
      type: String,
      default: null,
    },
    scheduleError: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const today = computed(() => localDateKey(new Date()));

    const timeOptions = computed(() =>
      scheduleTimeOptions(props.scheduledDate, props.scheduledTime)
    );

    const scheduledDateLabel = computed(() => {
      if (!props.scheduledDate) return "";
      const date = new Date(`${props.scheduledDate}T00:00:00`);
      return Number.isNaN(date.getTime())
        ? props.scheduledDate
        : date.toLocaleDateString("fr-FR", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          });
    });

    return {
      formatHelp: computed(() =>
        props.editorialFormat === EDITORIAL_FORMAT_DEAL
          ? "Bon plan : la réduction est l’information principale ; prix actuel et prix de comparaison requis."
          : "Focus produit : usages, caractéristiques et avis du produit."
      ),
      formatOptions: EDITORIAL_FORMAT_OPTIONS,
      icons: {
        mdiAccountEditOutline,
        mdiAlertOutline,
        mdiPencil,
        mdiPublish,
        mdiTextBoxEditOutline,
        mdiWeb,
      },
      indigo: RMR_INDIGO,
      postStatusOptions: POST_STATUS_OPTIONS,
      publicationHelp: computed(() => {
        const scheduled =
          props.scheduledDate && props.scheduledTime
            ? new Date(`${props.scheduledDate}T${props.scheduledTime}:00`)
            : null;
        return requestedPublicationLabel(
          props.postStatus,
          scheduled && !Number.isNaN(scheduled.getTime()) ? scheduled : null
        );
      }),
      scheduledDateLabel,
      signatureMaxLength: SIGNATURE_MAX_LENGTH,
      timeOptions,
      today,
      warningColor: RMR_ORANGE,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "./_ctc-step.scss";

// Every row shares one grid: icon, label and help, then the control column.
.ctc-settings {
  margin-top: 18px;
  overflow: hidden;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-2xl;
  background: $rmr-surface-0;
  box-shadow: $rmr-shadow-card;
}

.ctc-settings__row {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) minmax(0, 440px);
  align-items: center;
  gap: 12px 16px;
  padding: 16px 20px;

  & + & {
    border-top: 1px solid $rmr-border;
  }
}

.ctc-settings__icon {
  display: flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  align-self: start;
  border-radius: $rmr-radius-lg;
  background: $rmr-indigo-bg;
}

.ctc-settings__copy {
  min-width: 0;

  strong,
  small {
    display: block;
  }

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-title;
  }

  small {
    margin-top: 2px;
    color: $rmr-text-muted;
    font-size: $rmr-fs-base;
  }
}

.ctc-settings__control {
  min-width: 0;
}

.ctc-settings__site {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 6px 6px 14px;
  border: 1px solid $rmr-indigo-border;
  border-radius: $rmr-radius-lg;
  background: $rmr-indigo-bg;
}

.ctc-settings__site-name {
  min-width: 0;

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-base;
  }

  small {
    color: $rmr-text-muted;
    font-size: $rmr-fs-sm;
  }
}

.ctc-publication {
  display: grid;
  gap: 10px;
}

.ctc-publication__schedule {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 140px);
  gap: 8px;
}

.ctc-publication__warning,
.ctc-publication__error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: $rmr-fs-sm;
  font-weight: 650;
}

.ctc-publication__warning {
  color: $rmr-orange;
}

.ctc-publication__error {
  color: $rmr-danger;
}

@media (max-width: 860px) {
  .ctc-settings__row {
    grid-template-columns: 40px minmax(0, 1fr);
  }

  .ctc-settings__control {
    grid-column: 2;
  }
}

@media (max-width: 520px) {
  .ctc-settings__row {
    padding: 14px 16px;
  }

  .ctc-publication__schedule {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
